import React, { useState } from 'react';
import { 
  Users, BookOpen, Upload, CheckCircle2, Clock, 
  FileText, Award, Video, Plus, Check, MessageSquare 
} from 'lucide-react';
import { FACULTY_MEMBERS } from '../../data/portalData';

export const FacultyDashboard = () => {
  const currentFaculty = FACULTY_MEMBERS[1]; // Prof. Asuquo E. Johnson
  const [selectedCourse, setSelectedCourse] = useState('DBA 911');
  const [uploadedMaterials, setUploadedMaterials] = useState([
    { title: "Module 1: Corporate Turnaround & Crisis Diagnostics in Emerging Economies", type: "PDF Slides", size: "4.8 MB", date: "12 Oct 2026" },
    { title: "Module 2: Strategic Value Innovation & Blue Ocean Paradigms (Executive Harvard Case)", type: "Case Study", size: "2.1 MB", date: "15 Oct 2026" },
    { title: "Lecture Recording: Executive Session on M&A Due Diligence in Sub-Saharan Energy Sector", type: "Video (MP4)", size: "480 MB", date: "17 Oct 2026" }
  ]);

  const [submissions, setSubmissions] = useState([
    { student: "Emem Udoh (DBA Candidate)", matric: "AKSU/BS/2026/DBA/012", task: "Digital Turnaround Framework for Brownfield Enterprises", status: "Graded", grade: "89/100" },
    { student: "Idorenyin Bassey (PhD Candidate)", matric: "AKSU/BS/2026/PHD/004", task: "Comparative Boardroom Governance: UK Code vs Nigerian Code 2018", status: "Needs Review", grade: "Pending" },
    { student: "Aniefiok Okon (MBA Scholar)", matric: "AKSU/BS/2026/MBA/089", task: "Developing an Omnichannel Go-To-Market Pitch", status: "Needs Review", grade: "Pending" }
  ]);

  const [newMaterialTitle, setNewMaterialTitle] = useState('');
  const [targetModule, setTargetModule] = useState('Module 1: Corporate Turnaround & Crisis Diagnostics');
  const [targetSlot, setTargetSlot] = useState('Executive Lecture Slides (PDF/PPT)');
  const [targetCohort, setTargetCohort] = useState('2025/2026 Executive Intake (6 Cleared Candidates)');
  const [audienceScope, setAudienceScope] = useState('Enrolled & Tuition Cleared Candidates Only');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadSuccessAlert, setUploadSuccessAlert] = useState(null);

  const handleAddMaterial = (e) => {
    e.preventDefault();
    if (!newMaterialTitle.trim()) return;

    const newMaterial = {
      title: newMaterialTitle,
      course: selectedCourse,
      module: targetModule,
      slot: targetSlot,
      cohort: targetCohort,
      audience: audienceScope,
      type: targetSlot.includes('Slides') ? 'PDF Slides' : targetSlot.includes('Case') ? 'Case Study' : targetSlot.includes('Video') ? 'Video (MP4)' : 'Document',
      size: "4.2 MB",
      date: "Just Now",
      status: "Verified & Live"
    };

    setUploadedMaterials(prev => [newMaterial, ...prev]);
    setUploadSuccessAlert({
      title: newMaterialTitle,
      route: `${selectedCourse} › ${targetModule.split(':')[0]} › ${targetSlot}`,
      audience: audienceScope
    });
    setNewMaterialTitle('');
    setShowUploadForm(false);

    // Auto-dismiss alert after 6 seconds
    setTimeout(() => {
      setUploadSuccessAlert(null);
    }, 6000);
  };

  const handleGradeSubmission = (matric) => {
    setSubmissions(prev => prev.map(s => {
      if (s.matric === matric) {
        return { ...s, status: "Graded", grade: "94/100" };
      }
      return s;
    }));
  };

  return (
    <div className="space-y-8">
      
      {/* Faculty Profile Banner in AKSU Colors */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-6 sm:p-8 border-2 border-[#7A470E] shadow-xl flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <img 
              src={currentFaculty.avatar} 
              alt={currentFaculty.name} 
              className="w-16 h-16 rounded-full object-cover border-2 border-[#7A470E] shadow-md"
            />
            <span className="absolute bottom-0 right-0 bg-[#7A470E] w-4 h-4 rounded-full border-2 border-white" />
          </div>
          <div>
            <div className="text-xs uppercase font-black tracking-widest text-[#B87A28] font-sc">
              Executive Faculty & Academic Chair
            </div>
            <h2 className="text-xl sm:text-3xl font-black font-sc text-white">
              {currentFaculty.name}, <span className="font-sans text-xs font-normal text-[#B87A28]">{currentFaculty.qualifications}</span>
            </h2>
            <div className="text-xs text-slate-300 mt-0.5">
              {currentFaculty.rank} • Department of Business Administration
            </div>
          </div>
        </div>

        {/* Assigned Courses Switcher */}
        <div className="flex items-center space-x-2 bg-[#030F1C] p-1.5 rounded-xl border border-slate-800 text-xs">
          <span className="text-slate-300 px-2 font-bold font-sc">Assigned Modules:</span>
          {currentFaculty.coursesAssigned.map(course => (
            <button
              key={course}
              onClick={() => setSelectedCourse(course)}
              className={`px-3 py-1.5 rounded-lg font-black transition-all cursor-pointer font-sc ${
                selectedCourse === course
                  ? 'bg-[#0B2644] text-white shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              {course}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid: Uploads & Submissions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left: Course Lecture Materials & Slides */}
        <div className="lg:col-span-6 bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between border-b-2 border-slate-200 pb-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                {selectedCourse} Repository
              </span>
              <h3 className="font-black text-xl font-sc text-[#0B2644]">
                Executive Learning Materials & Slide Decks
              </h3>
            </div>
            <button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="px-3.5 py-1.5 bg-[#0B2644] hover:bg-[#06182B] text-white font-bold text-xs rounded-lg transition-colors flex items-center space-x-1 cursor-pointer font-sc shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Upload Material</span>
            </button>
          </div>

          {/* Live Upload Confirmation Alert */}
          {uploadSuccessAlert && (
            <div className="p-4 rounded-xl bg-emerald-50 border-2 border-emerald-300 text-emerald-900 text-xs space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex items-center space-x-2 font-bold font-sc text-sm text-[#0B2644]">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Material Verified & Successfully Routed</span>
              </div>
              <p className="text-[11px] text-slate-700">
                <strong>{uploadSuccessAlert.title}</strong> has been routed to module component:
              </p>
              <div className="font-mono text-[10px] bg-white px-2.5 py-1 rounded border border-emerald-200 text-[#0B2644] font-bold">
                Route: {uploadSuccessAlert.route} • Audience: {uploadSuccessAlert.audience}
              </div>
            </div>
          )}

          {showUploadForm && (
            <form onSubmit={handleAddMaterial} className="p-5 bg-slate-50 rounded-2xl border-2 border-slate-300 space-y-4 shadow-inner">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                  4-Tier Lesson Material Targeting Wizard
                </span>
                <span className="text-[10px] font-mono text-[#0B2644] bg-emerald-100 px-2 py-0.5 rounded font-bold">
                  Strict Scoped Route
                </span>
              </div>

              {/* Tier 1: Course & Cohort */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="text-slate-700 font-bold block mb-1 font-sc">
                    Tier 1: Assigned Course Scope
                  </label>
                  <select 
                    value={selectedCourse} 
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-slate-800 font-bold focus:border-[#7A470E]"
                  >
                    {currentFaculty.coursesAssigned.map(c => (
                      <option key={c} value={c}>{c} - Assigned Faculty Course</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-slate-700 font-bold block mb-1 font-sc">
                    Target Cohort / Session
                  </label>
                  <select 
                    value={targetCohort}
                    onChange={(e) => setTargetCohort(e.target.value)}
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-slate-800 focus:border-[#7A470E]"
                  >
                    <option value="2025/2026 Executive Intake (6 Cleared Candidates)">
                      2025/2026 Executive Intake (6 Cleared Candidates)
                    </option>
                    <option value="2025/2026 Doctoral Colloquium Group">
                      2025/2026 Doctoral Colloquium Group
                    </option>
                  </select>
                </div>
              </div>

              {/* Tier 2: Module Unit & Component Slot */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="text-slate-700 font-bold block mb-1 font-sc">
                    Tier 2: Target Syllabus Module Unit
                  </label>
                  <select
                    value={targetModule}
                    onChange={(e) => setTargetModule(e.target.value)}
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-slate-800 focus:border-[#7A470E]"
                  >
                    <option value="Module 1: Corporate Turnaround & Crisis Diagnostics">Module 1: Corporate Turnaround & Crisis Diagnostics</option>
                    <option value="Module 2: Strategic Value Innovation & Blue Ocean Paradigms">Module 2: Strategic Value Innovation & Blue Ocean</option>
                    <option value="Module 3: Boardroom Governance & Shareholder Activism">Module 3: Boardroom Governance & Activism</option>
                    <option value="Module 4: Executive M&A and Negotiations">Module 4: Executive M&A and Negotiations</option>
                  </select>
                </div>

                <div>
                  <label className="text-slate-700 font-bold block mb-1 font-sc">
                    Target LMS Component Slot
                  </label>
                  <select
                    value={targetSlot}
                    onChange={(e) => setTargetSlot(e.target.value)}
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-slate-800 font-bold focus:border-[#7A470E]"
                  >
                    <option value="Executive Lecture Slides (PDF/PPT)">Executive Lecture Slides (PDF/PPT)</option>
                    <option value="Harvard / Industry Case Study Dossier">Harvard / Industry Case Study Dossier</option>
                    <option value="High-Definition Video Lecture Recording">High-Definition Video Lecture Recording</option>
                    <option value="Quantitative Model / Empirical Dataset">Quantitative Model / Empirical Dataset</option>
                    <option value="Capstone Problem Brief / Deliverable">Capstone Problem Brief / Deliverable</option>
                  </select>
                </div>
              </div>

              {/* Tier 3: Intended Student Audience Enforcement */}
              <div className="text-xs">
                <label className="text-slate-700 font-bold block mb-1 font-sc">
                  Tier 3: Intended Student Audience & Clearance Guard
                </label>
                <select
                  value={audienceScope}
                  onChange={(e) => setAudienceScope(e.target.value)}
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg text-slate-800 focus:border-[#7A470E]"
                >
                  <option value="Enrolled & Tuition Cleared Candidates Only">
                    Enrolled & Tuition Cleared Candidates Only (Recommended: 6 Cleared Scholars)
                  </option>
                  <option value="Full Cohort Provisional Access">
                    Full Cohort Provisional Access (Orientation & Open Syllabus Only)
                  </option>
                </select>
              </div>

              {/* Tier 4: Material Title & File Name */}
              <div className="text-xs space-y-1">
                <label className="text-slate-700 font-bold block font-sc">
                  Material Title / Executive Presentation Topic:
                </label>
                <input
                  type="text"
                  value={newMaterialTitle}
                  onChange={(e) => setNewMaterialTitle(e.target.value)}
                  placeholder="e.g. Econometric Turnaround Regressions & Financial Distress Models"
                  required
                  className="w-full text-xs p-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-[#7A470E]"
                />
              </div>

              {/* Routing Preview Card */}
              <div className="p-3 bg-amber-50 rounded-xl border border-[#7A470E]/30 text-[11px] space-y-1">
                <div className="font-bold text-[#0B2644] font-sc flex items-center space-x-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#7A470E]" />
                  <span>Verified Target Distribution Route:</span>
                </div>
                <div className="font-mono text-slate-700 text-[10px]">
                  AKSU-LMS://[{selectedCourse}] › [{targetModule.split(':')[0]}] › [{targetSlot.split(' ')[0]}]
                </div>
                <div className="text-slate-600 text-[10px]">
                  ✓ Will be deposited directly into student LMS for <strong>{audienceScope.split('(')[0]}</strong>.
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="px-3.5 py-2 text-xs text-slate-600 hover:text-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0B2644] text-white font-bold text-xs rounded-xl hover:bg-[#06182B] font-sc shadow-sm cursor-pointer"
                >
                  Validate & Publish to Target Slot
                </button>
              </div>
            </form>
          )}

          <div className="space-y-3">
            {uploadedMaterials.map((mat, idx) => (
              <div 
                key={idx}
                className="p-3.5 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-1.5 hover:border-[#7A470E] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-0.5 pr-2">
                    <div className="font-bold text-slate-900 text-xs">{mat.title}</div>
                    <div className="text-[11px] text-slate-500">
                      Format: {mat.type} • Size: {mat.size} • Uploaded: {mat.date}
                    </div>
                  </div>
                  <span className="text-[10px] font-black text-[#7A470E] bg-emerald-100 px-2 py-0.5 rounded shrink-0 font-sc">
                    {mat.status || "Published"}
                  </span>
                </div>

                {mat.slot && (
                  <div className="pt-1.5 border-t border-slate-200/80 flex items-center justify-between text-[10px] font-mono text-slate-600">
                    <span>Target Slot: <strong className="text-[#0B2644]">{mat.slot}</strong></span>
                    <span className="text-[#7A470E] font-bold">Audience: 6 Candidates</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Executive Student Submissions & Grading Console */}
        <div className="lg:col-span-6 bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between border-b-2 border-slate-200 pb-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                Assessment Desk
              </span>
              <h3 className="font-black text-xl font-sc text-[#0B2644]">
                Executive Coursework & Capstone Submissions
              </h3>
            </div>
            <span className="text-xs font-mono bg-amber-50/50 text-[#0B2644] font-black px-2.5 py-1 rounded border border-slate-200">
              {submissions.filter(s => s.status === 'Needs Review').length} Awaiting Grade
            </span>
          </div>

          <div className="space-y-3">
            {submissions.map((sub, idx) => (
              <div 
                key={idx}
                className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2 text-xs"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <strong className="text-slate-900 block text-sm font-sc">{sub.student}</strong>
                    <span className="font-mono text-slate-500 text-[11px]">{sub.matric}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black font-sc ${
                    sub.status === 'Graded'
                      ? 'bg-emerald-100 text-[#7A470E]'
                      : 'bg-red-100 text-[#0B2644]'
                  }`}>
                    {sub.status}: {sub.grade}
                  </span>
                </div>

                <div className="text-slate-700 bg-white p-2.5 rounded-lg border border-slate-200">
                  <span className="text-slate-500 block text-[10px] uppercase font-bold font-sc">Submitted Assignment:</span>
                  {sub.task}
                </div>

                {sub.status === 'Needs Review' ? (
                  <div className="pt-2 flex justify-end">
                    <button
                      onClick={() => handleGradeSubmission(sub.matric)}
                      className="px-4 py-1.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold text-xs rounded-lg shadow-xs transition-colors flex items-center space-x-1 cursor-pointer font-sc"
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>Grade (Award 94/100)</span>
                    </button>
                  </div>
                ) : (
                  <div className="text-[11px] text-[#7A470E] font-bold flex items-center pt-1">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    <span>Graded & Recorded in Central Senate Master Sheet</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
};
